import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import ForceChangePassword from "@/components/ForceChangePassword";
import DeleteButton from "@/components/Header/Button/DeleteButton";
import InfoButton from "@/components/Header/Button/InfoButton";
import ResetButton from "@/components/Header/Button/ResetButton";
import UserAccessButton from "@/components/Header/Button/UserAccessButton";
import HeaderButtonList from "@/components/Header/HeaderButtonList/HeaderButtonList";
import ResendInviteButton from "@/components/ResendInviteButton";
import UserAccess from "@/components/UserAccess";
import { BUTTON_TYPE, BUTTON_KEYS } from "@/constants/Button";
import Info from "@/features/Users/Info";
import Reset from "@/features/Users/Reset";
import UserEdit from "@/features/Users/UserEdit";
import { useAuthorization } from "@/modules/authorization";
import { useSidePanel } from "@/modules/side-panel";
import { EditButton } from "@/ui/edit-button";

const HeaderButtons = ({ user = {} }) => {
  const { t } = useTranslation();
  const { openSidePanel } = useSidePanel();

  const {
    canUpdateItem,
    canDeleteItem,
    canResetUser,
    canResendInvite,
    canForceChangePassword,
  } = useAuthorization();

  const onEditClick = () => {
    openSidePanel({
      title: user.name,
      component: <UserEdit userId={user.id} />,
    });
  };

  const onInfoSelect = () => {
    openSidePanel({
      title: user.name,
      component: <Info user={user} />,
    });
  };

  const onUserAccessSelect = () => {
    openSidePanel({
      title: t("global:manageUserAccess"),
      component: <UserAccess user={user} />,
    });
  };

  const onResetSelect = () => {
    openSidePanel({
      title: user.name,
      component: <Reset user={user} />,
    });
  };

  const primaryActions = [];

  if (canResendInvite(user)) {
    primaryActions.push(
      <ResendInviteButton key={BUTTON_KEYS.RESEND_INVITE} userId={user.id} />
    );
  }

  primaryActions.push(<InfoButton key="info" onInfoSelect={onInfoSelect} />);

  const secondaryActions = [];

  secondaryActions.push({
    key: BUTTON_KEYS.USER_ACCESS,
    label: (
      <UserAccessButton
        onUserAccessSelect={onUserAccessSelect}
        key={BUTTON_KEYS.USER_ACCESS}
        buttonProps={{
          type: BUTTON_TYPE.TEXT,
        }}
      />
    ),
  });

  if (canUpdateItem(user)) {
    secondaryActions.push({
      key: BUTTON_KEYS.EDIT,
      label: (
        <EditButton key={BUTTON_KEYS.EDIT} type="text" onClick={onEditClick} />
      ),
    });
  }

  if (canResetUser(user)) {
    secondaryActions.push({
      key: BUTTON_KEYS.RESET,
      label: (
        <ResetButton
          key={BUTTON_KEYS.RESET}
          onResetSelect={onResetSelect}
          label={t("user:resetMFA.label")}
          buttonProps={{
            type: BUTTON_TYPE.TEXT,
          }}
        />
      ),
    });
  }

  if (canForceChangePassword(user)) {
    secondaryActions.push({
      key: BUTTON_KEYS.FORCE_CHANGE_PASSWORD,
      label: (
        <ForceChangePassword
          key={BUTTON_KEYS.FORCE_CHANGE_PASSWORD}
          user={user}
        />
      ),
    });
  }

  if (canDeleteItem(user)) {
    secondaryActions.push(
      {
        key: BUTTON_KEYS.DIVIDER,
        type: "divider",
      },
      {
        key: BUTTON_KEYS.DELETE,
        label: (
          <DeleteButton
            key={BUTTON_KEYS.DELETE}
            modelKey="user"
            model={user}
            buttonProps={{
              type: BUTTON_TYPE.TEXT,
            }}
          />
        ),
      }
    );
  }

  return (
    <HeaderButtonList
      primaryActions={primaryActions}
      secondaryActions={secondaryActions}
    />
  );
};

HeaderButtons.propTypes = {
  user: PropTypes.object.isRequired,
};

export default HeaderButtons;
